/**
 * Task #61 — single source of truth for which main pane `SiteRagEngine`
 * renders. Extracted from the inline JSX conditions so the view-selection
 * decision is unit-testable WITHOUT mounting the full engine (which pulls in
 * Google Maps + 30 lazily-loaded panes).
 *
 * The bug this fixes: clicking a site card while a report is open flips the
 * active site (clearing/normalizing the stale `activeReportId`), but for a
 * frame or two `engineView` can still read `'report'` before the
 * derivation effect / `useUrlSiteHydration` settle it back to `'site'`.
 * During that window `activeReport` is undefined, and the old code rendered
 * the "Select or Generate a Report" placeholder — so it FLASHED on every
 * report→site switch.
 *
 * Rule: an `engineView === 'report'` state with NO resolved report is always
 * a transient. If a site is active we render the site landing through it
 * (seamless, no blank frame); only when there is genuinely no active site do
 * we fall back to the terminal placeholder (a state the derivation effect
 * otherwise resolves to `'dashboard'`).
 */
import type { EngineView } from './useUrlSiteHydration';

export type EngineRenderView =
  | 'dashboard'
  | 'site-landing'
  | 'report'
  | 'comparison'
  | 'empty-report-placeholder'
  /** Render nothing — e.g. `'site'` view with no resolved site. */
  | 'none';

export interface EngineRenderInputs {
  engineView: EngineView;
  /** True when `siteRag.activeSiteId` resolves to a real site object. */
  hasActiveSite: boolean;
  /** True when `siteRag.activeReportId` resolves to a real report object. */
  hasActiveReport: boolean;
}

/**
 * Pure: given the current view + whether a site/report is resolved, return
 * which pane the engine's `<main>` should render. Mirrors the JSX guards in
 * `SiteRagEngine.tsx` exactly.
 */
export function selectEngineRenderView(input: EngineRenderInputs): EngineRenderView {
  const { engineView, hasActiveSite, hasActiveReport } = input;

  if (engineView === 'dashboard') return 'dashboard';
  if (engineView === 'comparison') return 'comparison';

  // A genuinely-open report wins (deep-link or freshly-selected report).
  if (engineView === 'report' && hasActiveReport) return 'report';

  // Site landing covers both the normal 'site' view AND the transient
  // 'report'-without-a-report window — provided a site is active.
  if (engineView === 'site' || engineView === 'report') {
    if (hasActiveSite) return 'site-landing';
  }

  // No report, no site to fall back to → terminal placeholder. The
  // derivation effect normally resolves this to 'dashboard', so in practice
  // this is reached only transiently / defensively.
  if (engineView === 'report') return 'empty-report-placeholder';

  // engineView === 'site' but no active site → render nothing (matches the
  // pre-existing `if (!activeSite) return null;` guard in the site branch).
  return 'none';
}

/**
 * Convenience predicate used by the regression test: does the
 * "Select or Generate a Report" placeholder render for these inputs?
 */
export function rendersEmptyReportPlaceholder(input: EngineRenderInputs): boolean {
  return (
    input.engineView === 'report' &&
    !input.hasActiveReport &&
    !input.hasActiveSite
  );
}
